import { Stack, Typography } from '@mui/material'
import { C1Chip } from 'c1ui/components/C1Chip'
import { AppAvatar } from 'components/core/avatar'
import { C1Card } from 'components/core/card/C1Card'
import { ExpiresInTime } from 'components/core/time/display'
import { AccessRequestActions } from 'components/pages/accessrequest/expiring-access/access-request-actions'
import { EndUserDashboardSection } from 'components/pages/end-user-dashboard/EndUserDashboardSection'
import { AppEntitlementWithUserBindingValue } from 'lib/actions/apps/app-entitlement'
import { SystemAnyAccessEntitlementID } from 'lib/constants/system'
import { UserContext } from 'lib/context/app'
import { routes } from 'lib/routes'
import { AppEntitlementSearchServiceSearchExpiringAccessForUserCountRequest, AppEntitlementSearchServiceSearchExpiringAccessForUserRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { Timestamp as pbtsTimestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { searchExpiringAccessForUser, searchExpiringAccessForUserCount } from 'redux_toolkit/entitlements'

export const EndUserDashboardExpiringAccess: FC = () => {
    const intl = useIntl()
    const { user } = useContext(UserContext)
    const userId = user?.id

    const filter = {
        targetUserId: userId,
        expiresBefore: pbtsTimestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
    }

    return <EndUserDashboardSection<AppEntitlementWithUserBindingValue,
        AppEntitlementSearchServiceSearchExpiringAccessForUserRequest,
        AppEntitlementSearchServiceSearchExpiringAccessForUserCountRequest
    >
        title={intl.formatMessage({
            id: 'end_user_dashboard_my_reviews_title',
            defaultMessage: 'Expiring access',
        })}
        subtitle={intl.formatMessage({
            id: 'end_user_dashboard_my_reviews_subtitle',
            defaultMessage: 'In 7 days or less',
        })}
        seeAllHref={routes.requests.temporary}
        seeAllLabel={(count): string => (count === 1
            ? intl.formatMessage({
                id: 'end_user_dashboard_expiring_access_see_all_one',
                defaultMessage: 'See expiring access',
            })
            : intl.formatMessage({
                id: 'end_user_dashboard_expiring_access_see_all',
                defaultMessage: 'See all {count} expiring access',
            }, { count }))}
        ListItemComponent={EndUserDashboardExpiringAccessListItem}
        getDataHook={searchExpiringAccessForUser.useSearchExpiringAccessForUser}
        getDataRequest={AppEntitlementSearchServiceSearchExpiringAccessForUserRequest.create({
            ...filter,
            expandMask: { paths: ['*'] },
        })}
        getCountHook={searchExpiringAccessForUserCount.useSearchExpiringAccessForUserCount}
        getCountRequest={AppEntitlementSearchServiceSearchExpiringAccessForUserCountRequest.create({
            ...filter,
        })}
        invalidateHook={searchExpiringAccessForUser.useInvalidateSearchExpiringAccessForUser}
    />
}

const EndUserDashboardExpiringAccessListItem: FC<{ row: AppEntitlementWithUserBindingValue }> = ({ row }) => {
    const binding = row.appEntitlementUserBinding
    const { user } = useContext(UserContext)

    if (!user) return null

    return <C1Card
        sx={{
            my: 0,
            p: 3,
            minHeight: '60px',
            borderRadius: 0,
            borderBottom: 'none',
            borderColor: 'border.subtle',
            '&:last-of-type': {
                borderBottom: '1px solid',
                borderColor: 'border.subtle',
            },
        }}
    >
        <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between" width="100%">
            <Stack direction="row" gap={2} alignItems="center">
                {row.entitlement?.app && <AppAvatar appId={row.entitlement?.app?.id} />}

                <Stack direction="row" alignItems="center">
                    <Typography variant="body1" component="div">
                        {row.entitlement?.app?.displayName}
                        {' '}
                        {row.entitlement?.id === SystemAnyAccessEntitlementID
                            ? <FormattedMessage
                                id="end_user_dashboard_expiring_access_any_access"
                                defaultMessage="application access"
                            />
                            : row.entitlement?.displayName}
                    </Typography>
                </Stack>
            </Stack>

            <Stack direction="row" gap={2} alignItems="center">
                {binding?.appEntitlementUserBindingDeprovisionAt && <C1Chip
                    color="warning"
                    label={<ExpiresInTime timestamp={binding?.appEntitlementUserBindingDeprovisionAt} />}
                />}

                <AccessRequestActions requestCatalogEntitlement={row} user={user} />
            </Stack>
        </Stack>
    </C1Card>
}
